import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useApp }  from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import RotatePlayLogo from './RotatePlayLogo';

const DESKTOP_TABS = [
  { slug: 'dashboard', label: 'Dashboard' },
  { slug: 'players',   label: 'Players'   },
  { slug: 'session',   label: 'Session'   },
  { slug: 'settings',  label: 'Settings'  },
  { slug: 'history',   label: 'History'   },
];

const MOBILE_TABS = [
  { slug: 'leagues',   label: 'Leagues',   icon: '🏆', path: '/leagues' },
  { slug: 'dashboard', label: 'Dashboard', icon: '🏠', path: null },
  { slug: 'players',   label: 'Players',   icon: '👥', path: null },
  { slug: 'session',   label: 'Session',   icon: '🏟', path: null },
  { slug: 'history',   label: 'History',   icon: '📋', path: null },
];

export default function Nav() {
  const { league }              = useApp();
  const { organizer, logout }   = useAuth();
  const { leagueId }            = useParams<{ leagueId: string }>();
  const navigate                = useNavigate();
  const location                = useLocation();

  const base = leagueId ? `/leagues/${leagueId}` : '';

  return (
    <>
      <nav style={{
        background: 'var(--green)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        height: 56,
        boxShadow: '0 2px 6px rgba(0,0,0,.15)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>

        {/* Brand — clicks to My Leagues */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto',
            whiteSpace: 'nowrap', cursor: 'pointer',
            background: 'rgba(255,255,255,.15)', borderRadius: 8,
            padding: '5px 10px 5px 6px',
            transition: 'background .15s',
          }}
          onClick={() => navigate('/leagues')}
          title="My Leagues"
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.25)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,.15)')}
        >
          <RotatePlayLogo size={26} animated />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ fontWeight: 800, fontSize: '.9rem', letterSpacing: '.2px' }}>RotatePlay</span>
            {league && (
              <span style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--yellow)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {league.name}
              </span>
            )}
          </div>
        </div>

        {/* Tabs — desktop only */}
        <div className="nav-tabs-desktop" style={{ display: 'flex', height: '100%', overflowX: 'auto' }}>
          {DESKTOP_TABS.map(tab => {
            const path   = `${base}/${tab.slug}`;
            const active = location.pathname.endsWith(`/${tab.slug}`);
            return (
              <button
                key={tab.slug}
                onClick={() => navigate(path)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: active ? '#fff' : 'rgba(255,255,255,.75)',
                  cursor: 'pointer',
                  fontSize: '.82rem',
                  fontWeight: 500,
                  padding: '0 11px',
                  height: '100%',
                  borderBottom: active ? '3px solid var(--yellow)' : '3px solid transparent',
                  whiteSpace: 'nowrap',
                  transition: 'color .15s, border-color .15s',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Settings icon — mobile only, shown next to Sign out */}
        <button
          className="nav-settings-mobile"
          onClick={() => navigate(`${base}/settings`)}
          title="Settings"
          style={{
            marginLeft: 8,
            background: 'transparent',
            border: '1.5px solid rgba(255,255,255,.55)',
            color: '#fff',
            borderRadius: 6,
            width: 34, height: 34,
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'background .15s',
          }}
        >
          ⚙️
        </button>

        {/* Plan badge */}
        {organizer && (
          <button
            onClick={() => navigate('/account')}
            title="Account & Billing"
            style={{
              marginLeft: 8,
              background: organizer.plan === 'pro' ? 'var(--yellow)' : 'rgba(255,255,255,.15)',
              border: 'none',
              color: organizer.plan === 'pro' ? 'var(--brown)' : 'rgba(255,255,255,.85)',
              borderRadius: 20,
              padding: '3px 10px',
              cursor: 'pointer',
              fontSize: '.72rem',
              fontWeight: 800,
              letterSpacing: '.04em',
              transition: 'background .15s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '.8'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
          >
            {organizer.plan === 'pro' ? 'PRO' : 'FREE'}
          </button>
        )}

        {/* Account */}
        {organizer && (
          <button
            onClick={() => navigate('/account')}
            title="Account & Billing"
            style={{
              marginLeft: 8,
              background: 'transparent',
              border: '1.5px solid rgba(255,255,255,.55)',
              color: '#fff',
              borderRadius: 6,
              width: 34, height: 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'background .15s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            👤
          </button>
        )}

        {/* Sign out */}
        {organizer && (
          <button
            onClick={logout}
            title={`Signed in as ${organizer.email}`}
            style={{
              marginLeft: 8,
              background: 'transparent',
              border: '1.5px solid rgba(255,255,255,.55)',
              color: '#fff',
              borderRadius: 6,
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: '.78rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: 'background .15s, border-color .15s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,.2)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,.8)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,.55)';
            }}
          >
            Sign out
          </button>
        )}
      </nav>

      {/* Bottom nav — mobile only */}
      <nav className="nav-bottom">
        {MOBILE_TABS.map(tab => {
          const dest   = tab.path ?? `${base}/${tab.slug}`;
          const active = tab.slug === 'leagues'
            ? location.pathname === '/leagues'
            : location.pathname.endsWith(`/${tab.slug}`);
          return (
            <button
              key={tab.slug}
              className={`nav-bottom-tab${active ? ' active' : ''}`}
              onClick={() => navigate(dest)}
            >
              <span className="nav-bottom-icon">{tab.icon}</span>
              <span className="nav-bottom-label">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
